import type { TenantDomainDto } from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { LogOut, Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { type ReactNode, useEffect, useState } from 'react';
import { ErrorAlert } from '@/components/error-alert';
import { PageHeader } from '@/components/page-header';
import { TenantSwitcher } from '@/components/tenant-switcher';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSidebar } from '@/components/ui/sidebar';
import { useLogout } from '@/components/user-menu';
import { api } from '@/lib/api';
import { formatDateTime, ROLE_LABELS } from '@/lib/format';
import { type Preferences, setPreference, usePreferences } from '@/lib/preferences';
import { useMe } from '@/lib/queries';
import { cn } from '@/lib/utils';

/** A titled group of setting rows, like the studio's settings page. */
function Group({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-2">
      <div>
        <h2 className="text-base font-medium">{title}</h2>
        <p className="text-muted-foreground text-xs">{description}</p>
      </div>
      <div className="bg-card shadow-panel divide-y rounded-xl">{children}</div>
    </section>
  );
}

function Row({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-3">
      <div className="grid gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="text-muted-foreground text-xs">{hint}</span>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

/** A small set of mutually exclusive choices, shown as a pill switch. */
function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string; icon?: typeof Sun; iconOnly?: boolean }>;
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="bg-muted flex rounded-full p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          aria-label={o.iconOnly ? o.label : undefined}
          title={o.iconOnly ? o.label : undefined}
          onClick={() => onChange(o.value)}
          className={cn(
            'text-muted-foreground hover:text-foreground aria-checked:bg-card aria-checked:text-foreground aria-checked:shadow-panel focus-visible:ring-ring/50 inline-flex h-7 items-center gap-1.5 rounded-full text-xs font-medium outline-none focus-visible:ring-3 [&_svg]:size-3.5',
            o.iconOnly ? 'w-8 justify-center' : 'px-3',
          )}
        >
          {o.icon && <o.icon aria-hidden />}
          {!o.iconOnly && o.label}
        </button>
      ))}
    </div>
  );
}

/** Owners pick the email domain whose Google sign-ins join this workspace as viewers. */
function SignInDomainRow({ tenantId, canEdit }: { tenantId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const key = ['tenant-domain', tenantId];
  const current = useQuery({
    queryKey: key,
    queryFn: () => api<TenantDomainDto>('/tenant/domain'),
  });
  const [draft, setDraft] = useState('');
  useEffect(() => {
    setDraft(current.data?.domain ?? '');
  }, [current.data?.domain]);

  const save = useMutation({
    mutationFn: (domain: string | null) =>
      api<TenantDomainDto>('/tenant/domain', { method: 'PUT', body: { domain } }),
    onSuccess: (data) => queryClient.setQueryData(key, data),
  });
  const saved = current.data?.domain ?? '';
  const next = draft.trim().toLowerCase().replace(/^@/, '');

  return (
    <div className="grid gap-2 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="grid gap-0.5">
          <span className="text-sm font-medium">Google sign-in domain</span>
          <span className="text-muted-foreground text-xs">
            People who sign in with a Google account on this domain join as viewers. It must be the
            domain of your own email address. Owners can promote people on the Members page.
          </span>
        </div>
        {canEdit ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate(next || null);
            }}
          >
            <Input
              aria-label="Google sign-in domain"
              className="w-48"
              placeholder="example.com"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={save.isPending || next === saved}>
              Save
            </Button>
          </form>
        ) : (
          <span className="font-mono text-sm">{saved || 'Not set'}</span>
        )}
      </div>
      <ErrorAlert error={save.error} />
    </div>
  );
}

export function SettingsPage() {
  const me = useMe();
  const { theme = 'system', setTheme } = useTheme();
  const prefs = usePreferences();
  const sidebar = useSidebar();
  const logout = useLogout();
  const tenant = me.activeTenant;
  const set =
    <K extends keyof Preferences>(key: K) =>
    (value: Preferences[K]) =>
      setPreference(key, value);

  return (
    <>
      <PageHeader title="Settings" />
      <div className="mx-auto grid w-full max-w-3xl gap-8 py-2">
        <Group title="General" description="Stored in this browser.">
          <Row label="Theme" hint="Match your system, or always use light or dark.">
            <Choice
              label="Theme"
              value={theme as 'system' | 'light' | 'dark'}
              onChange={setTheme}
              options={[
                { value: 'system', label: 'System', icon: Monitor, iconOnly: true },
                { value: 'light', label: 'Light', icon: Sun, iconOnly: true },
                { value: 'dark', label: 'Dark', icon: Moon, iconOnly: true },
              ]}
            />
          </Row>
          <Row
            label="Send chat messages with"
            hint="In the agent chat and the Playground. The other key combination adds a new line."
          >
            <Choice
              label="Send chat messages with"
              value={prefs.sendWith}
              onChange={set('sendWith')}
              options={[
                { value: 'enter', label: 'Enter' },
                { value: 'mod-enter', label: '⌘/Ctrl + Enter' },
              ]}
            />
          </Row>
          <Row
            label="Show times in"
            hint={`For example ${formatDateTime(new Date().toISOString())}.`}
          >
            <Choice
              label="Show times in"
              value={prefs.timeZone}
              onChange={set('timeZone')}
              options={[
                { value: 'local', label: 'Local time' },
                { value: 'utc', label: 'UTC' },
              ]}
            />
          </Row>
          <Row label="Navigation" hint="Compact shows icons only. Applies now and on every visit.">
            <Choice
              label="Navigation"
              value={prefs.compactSidebar ? 'compact' : 'expanded'}
              onChange={(v) => {
                setPreference('compactSidebar', v === 'compact');
                sidebar.setOpen(v === 'expanded');
              }}
              options={[
                { value: 'expanded', label: 'Expanded' },
                { value: 'compact', label: 'Compact' },
              ]}
            />
          </Row>
        </Group>

        <Group title="Account" description="Who you are signed in as.">
          <Row label="Name">
            <span className="text-sm">{me.user.name || '—'}</span>
          </Row>
          <Row label="Email">
            <span className="text-sm">{me.user.email}</span>
          </Row>
          {me.user.isSuperAdmin && (
            <Row label="Platform role" hint="Can create and suspend workspaces.">
              <Badge variant="secondary">Super admin</Badge>
            </Row>
          )}
          <Row label="Session" hint="Signs you out in this browser.">
            <Button
              variant="outline"
              size="sm"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              <LogOut aria-hidden />
              Sign out
            </Button>
          </Row>
        </Group>

        <Group title="Workspace" description="The workspace this console is acting on.">
          <Row label="Current workspace" hint="Switching takes you back to the overview.">
            <div className="w-56">
              <TenantSwitcher />
            </div>
          </Row>
          {tenant && (
            <>
              <Row label="URL name">
                <span className="font-mono text-sm">{tenant.slug}</span>
              </Row>
              <Row label="Your role">
                <Badge variant="secondary">{ROLE_LABELS[tenant.role]}</Badge>
              </Row>
              <SignInDomainRow tenantId={tenant.id} canEdit={tenant.role === 'owner'} />
              <Row label="People and model providers" hint="Managed on their own pages.">
                <Button variant="outline" size="sm" asChild>
                  <Link to="/members">Members</Link>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/llm-setup">LLM setup</Link>
                </Button>
              </Row>
            </>
          )}
        </Group>
      </div>
    </>
  );
}
